import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
const DigitalSignature = forwardRef(({ disabled = false, onSignatureChange }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const sigCanvas = useRef(null);
    const [hasSignature, setHasSignature] = useState(false);
    // Expose clearSignature, getSignature, and setSignature methods to parent
    useImperativeHandle(ref, () => ({
        clearSignature: () => {
            sigCanvas.current?.clear();
            setHasSignature(false);
            onSignatureChange?.(false);
        },
        getSignature: () => {
            if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
                // Return base64 data URL of the signature
                return sigCanvas.current.toDataURL('image/png');
            }
            return null;
        },
        setSignature: (dataUrl) => {
            if (sigCanvas.current && dataUrl) {
                // Clear the canvas first to reset SignaturePad's internal state
                sigCanvas.current.clear();
                // Wait a bit for clear to complete, then restore the image
                setTimeout(() => {
                    if (!sigCanvas.current)
                        return;
                    // Try to use SignaturePad's fromDataURL method if available
                    // Access the internal signaturePad instance through the ref
                    const signaturePad = sigCanvas.current?._sigPad || sigCanvas.current?.signaturePad;
                    if (signaturePad && typeof signaturePad.fromDataURL === 'function') {
                        try {
                            // fromDataURL restores strokes properly, maintaining SignaturePad's internal state
                            signaturePad.fromDataURL(dataUrl);
                            setHasSignature(true);
                            setTimeout(() => {
                                onSignatureChange?.(true);
                            }, 50);
                            return;
                        }
                        catch (e) {
                            console.warn('Failed to use fromDataURL, falling back to drawImage', e);
                        }
                    }
                    // Fallback: Draw directly to canvas
                    // We need to properly handle SignaturePad's internal state
                    const canvas = sigCanvas.current.getCanvas();
                    const ctx = canvas.getContext('2d');
                    const sigPadInstance = sigCanvas.current?._sigPad || sigCanvas.current?.signaturePad;
                    if (sigPadInstance) {
                        // Temporarily disable SignaturePad to prevent interference
                        sigPadInstance.off();
                    }
                    const img = new Image();
                    img.onload = () => {
                        if (!sigCanvas.current || !ctx)
                            return;
                        const width = canvas.width;
                        const height = canvas.height;
                        // Draw the image to the canvas
                        ctx.drawImage(img, 0, 0, width, height);
                        // Re-enable SignaturePad and reset its internal state
                        if (sigPadInstance) {
                            // Reset internal data arrays to ensure SignaturePad works after restoration
                            if (sigPadInstance._data) {
                                sigPadInstance._data = [];
                            }
                            if (sigPadInstance._points) {
                                sigPadInstance._points = [];
                            }
                            // Re-enable SignaturePad
                            sigPadInstance.on();
                        }
                        setHasSignature(true);
                        setTimeout(() => {
                            onSignatureChange?.(true);
                        }, 50);
                    };
                    img.onerror = () => {
                        // Re-enable even on error
                        if (sigPadInstance) {
                            sigPadInstance.on();
                        }
                        console.error('Failed to load signature image');
                    };
                    img.src = dataUrl;
                }, 10);
            }
        }
    }));
    useEffect(() => {
        if (sigCanvas.current && disabled) {
            sigCanvas.current?.off();
        }
        else {
            sigCanvas.current?.on();
        }
    }, [disabled]);
    const handleEnd = () => {
        const isEmpty = sigCanvas.current?.isEmpty() ?? true;
        setHasSignature(!isEmpty);
        onSignatureChange?.(!isEmpty);
    };
    return (_jsxs(Box, { sx: {
            position: 'relative',
            bgcolor: alpha(theme.palette.grey[500], 0.08),
            borderRadius: 1.5,
            p: '0px',
            mt: 1.5,
            width: '100%'
        }, children: [_jsx(SignatureCanvas, { ref: sigCanvas, penColor: 'black', onEnd: handleEnd, canvasProps: {
                    width: undefined,
                    height: 200,
                    className: 'signatureCanvas',
                    style: { width: '100%' }
                } }), !hasSignature && (_jsx(Typography, { variant: 'body2', sx: {
                    position: 'absolute',
                    top: '50%',
                    color: theme.palette.grey[500],
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none'
                }, children: t('undertaking.signHere') }))] }));
});
export default DigitalSignature;
